import React, { useState } from 'react';
import { TrendingUp, Loader2, MailCheck, KeyRound, CheckCircle2 } from 'lucide-react';
import { API_BASE } from '@/env';

/**
 * The two halves of password recovery.
 *
 * <p>ForgotPasswordPage always lands on the same "check your email" state whatever was typed —
 * the API is deliberately silent about whether an address has an account, and the UI must not
 * undo that by behaving differently.</p>
 */

const Shell: React.FC<{ subtitle: string; children: React.ReactNode }> = ({ subtitle, children }) => (
  <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center px-4">
    <div className="flex flex-col items-center mb-6">
      <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center mb-3">
        <TrendingUp className="h-6 w-6 text-white" />
      </div>
      <span className="text-xl font-bold">Trade<span className="text-primary">Execute</span></span>
      <span className="text-xs text-muted-foreground mt-1">{subtitle}</span>
    </div>
    <div className="w-full max-w-sm rounded-xl border border-border bg-card p-6">{children}</div>
  </div>
);

export const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || submitting) return;
    setSubmitting(true);
    try {
      await fetch(`${API_BASE}/api/Auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
    } catch {
      // Network failure or not, the screen says the same thing — see the header comment.
    }
    setSent(true);
    setSubmitting(false);
  };

  return (
    <Shell subtitle="Reset your password">
      {sent ? (
        <div className="space-y-3 text-center">
          <MailCheck className="h-8 w-8 text-primary mx-auto" />
          <div className="text-sm font-medium">Check your email</div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            If <span className="text-foreground">{email.trim()}</span> has an account, a reset
            link is on its way. It's valid for one hour — check spam if it doesn't arrive in a
            minute or two.
          </p>
          <a href="#/login" className="inline-block text-xs text-primary hover:underline pt-1">
            Back to sign in
          </a>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <p className="text-xs text-muted-foreground leading-relaxed">
            Enter your account email and we'll send you a link to choose a new password.
          </p>
          <div>
            <label htmlFor="fp-email" className="block text-xs font-medium mb-1.5">Email</label>
            <input
              id="fp-email"
              type="email"
              required
              autoFocus
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Send reset link
          </button>
          <div className="text-center text-xs text-muted-foreground">
            Remembered it?{' '}
            <a href="#/login" className="text-primary hover:underline font-medium">Sign in</a>
          </div>
        </form>
      )}
    </Shell>
  );
};

function tokenFromHash(): string {
  const q = (window.location.hash.split('?')[1] ?? '');
  return new URLSearchParams(q).get('token') ?? '';
}

export const ResetPasswordPage: React.FC = () => {
  const [token] = useState(() => tokenFromHash());
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/Auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: password }),
      });
      if (res.ok) {
        setDone(true);
      } else {
        const body = await res.json().catch(() => null);
        setError(body?.error ?? 'That reset link is invalid or has expired.');
      }
    } catch {
      setError('Could not reach the server. Please try again.');
    }
    setSubmitting(false);
  };

  if (!token) {
    return (
      <Shell subtitle="Reset your password">
        <div className="space-y-3 text-center">
          <div className="text-sm font-medium">This link is incomplete</div>
          <p className="text-xs text-muted-foreground">
            Open the reset link from your email again, or request a new one.
          </p>
          <a href="#/forgot-password" className="inline-block text-xs text-primary hover:underline">
            Request a new link
          </a>
        </div>
      </Shell>
    );
  }

  return (
    <Shell subtitle="Choose a new password">
      {done ? (
        <div className="space-y-3 text-center">
          <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto" />
          <div className="text-sm font-medium">Password changed</div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Every signed-in session has been logged out. Sign in with your new password.
          </p>
          <a href="#/login" className="inline-block text-xs text-primary hover:underline pt-1">
            Go to sign in
          </a>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {error}
            </div>
          )}
          <div>
            <label htmlFor="rp-pw" className="block text-xs font-medium mb-1.5">New password</label>
            <input
              id="rp-pw"
              type="password"
              required
              autoFocus
              minLength={8}
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
            />
          </div>
          <div>
            <label htmlFor="rp-pw2" className="block text-xs font-medium mb-1.5">Confirm password</label>
            <input
              id="rp-pw2"
              type="password"
              required
              minLength={8}
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
            Set new password
          </button>
        </form>
      )}
    </Shell>
  );
};
